import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import MainLayout from '../../layouts/MainLayout';
import {
  Card,
  Button,
  Space,
  Row,
  Col,
  Descriptions,
  Tag,
  Avatar,
  Spin,
  message,
  Tabs,
  Table,
  Empty,
  Modal,
  Input,
} from 'antd';
import {
  ArrowRightOutlined,
  EditOutlined,
  TeamOutlined,
  ProjectOutlined,
  DollarOutlined,
  FileTextOutlined,
  ShoppingCartOutlined,
  CustomerServiceOutlined,
  FolderOutlined,
  DownloadOutlined,
  FundOutlined,
  TrophyOutlined,
  CloseCircleOutlined,
  ShoppingOutlined,
  PhoneOutlined,
  LinkOutlined,
  PlusOutlined,
  CopyOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { clientsApi, financeSettingsApi } from '../../utils/api';
import { getImagePath } from '../../utils/imageHelper';
import PermissionGate from '../../components/PermissionGate';
import PriorityStars from '../../components/common/PriorityStars';
import EntityTagsList from '../../components/common/EntityTagsList';
import CopyIconButton from '../../components/common/CopyIconButton';
import ClickToCallButton from '../../components/call-center/ClickToCallButton';
import SmsHistoryPanel from '../../components/common/SmsHistoryPanel';
import { getDealStageVisualType } from '../../utils/dealStageUtils';
import { formatCloseDateLabel, formatDescriptiveDate, toJalali } from '../../utils/dateUtils';
import { resolveRelationLabel } from '../../utils/sourceUtils';
import { PANEL_PRIMARY_BTN_CLASS } from '../../constants/ui';
import { EmbeddedEstimatesTable, EmbeddedInvoicesTable } from '../../components/finance/EmbeddedFinanceDocs';
import { formatPriceToman } from '../../utils/formatPriceToman';
import { buildClientConsultantPrompt } from '../../utils/consultantPromptUtils';
import { useDetailReturnRefs } from '../../hooks/useListReturnState';
import { useEntityBreadcrumb } from '../../hooks/useEntityBreadcrumb';
import EntityBreadcrumb from '../../components/common/EntityBreadcrumb';

const statusMeta = {
  active: { text: 'فعال', color: 'green' },
  deactive: { text: 'غیرفعال', color: 'default' },
};

const resolveClientSourceLabel = (client) => {
  if (!client) return null;
  const detailsSource = client.client_details?.lead_source;
  return (
    resolveRelationLabel(client.source)
    || client.source_name
    || client.lead_source?.type
    || client.lead_source?.name
    || detailsSource?.type
    || detailsSource?.name
    || null
  );
};

/** نام شرکت از client.client_details.company_name (پاسخ tab=profile) */
const resolveClientCompanyName = (...sources) => {
  for (const src of sources) {
    if (!src || typeof src !== 'object') continue;
    const raw =
      src?.client?.client_details?.company_name
      || src?.client_details?.company_name
      || src?.company_name
      || null;
    const name = String(raw || '').trim();
    if (name) return name;
  }
  return null;
};

const extractClientDetails = (tabData) =>
  tabData?.client?.client_details || tabData?.client_details || null;

const MiniBarChart = ({ title, labels = [], values = [], colors = [] }) => {
  const total = (values || []).reduce((a, b) => a + (Number(b) || 0), 0) || 1;
  return (
    <Card size="small" title={<span style={{ fontFamily: 'CustomFont' }}>{title}</span>} className="h-full border-0 shadow-sm">
      <div className="space-y-2">
        {(labels || []).map((label, i) => {
          const v = Number(values[i]) || 0;
          const pct = Math.round((v / total) * 1000) / 10;
          const bg = colors[i] || '#1890ff';
          return (
            <div key={i}>
              <div className="flex justify-between text-xs mb-0.5" style={{ fontFamily: 'CustomFont' }}>
                <span>{label}</span>
                <span>{v}</span>
              </div>
              <div className="h-2 rounded bg-gray-100 dark:bg-gray-700 overflow-hidden">
                <div className="h-full rounded transition-all" style={{ width: `${pct}%`, background: bg }} />
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

const ProfileTab = ({ tabData, companyName: companyNameProp }) => {
  const client = tabData?.client;
  const companyName = companyNameProp || resolveClientCompanyName(tabData, client);
  const stats = tabData?.clientStats || {};
  const projectChart = tabData?.projectChart || {};
  const invoiceChart = tabData?.invoiceChart || {};
  const earningTotal = tabData?.earningTotal;
  const st = statusMeta[client?.status] || { text: client?.status, color: 'default' };

  return (
    <div className="space-y-6">
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <Card size="small" className="border-0 shadow-sm h-full">
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300 mb-1">
              <ProjectOutlined />
              <span>پروژه‌ها</span>
            </div>
            <div className="text-2xl font-bold">{stats.totalProjects ?? 0}</div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small" className="border-0 shadow-sm h-full">
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300 mb-1">
              <DollarOutlined />
              <span>فاکتورهای پرداخت‌نشده</span>
            </div>
            <div className="text-2xl font-bold">{stats.totalUnpaidInvoices ?? 0}</div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small" className="border-0 shadow-sm h-full">
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300 mb-1">
              <TeamOutlined />
              <span>قراردادها</span>
            </div>
            <div className="text-2xl font-bold">{stats.totalContracts ?? 0}</div>
          </Card>
        </Col>
      </Row>

      {earningTotal != null ? (
        <Card size="small" className="bg-emerald-50 dark:bg-emerald-900/20 border-emerald-100 dark:border-emerald-800 border-0 shadow-sm">
          <span className="text-emerald-800 dark:text-emerald-200 font-semibold">
            جمع درآمد: {formatPriceToman(earningTotal)}
          </span>
        </Card>
      ) : null}

      <Card className="shadow-sm border-0 bg-white dark:bg-gray-800">
        <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small">
          {companyName ? (
            <Descriptions.Item label="نام شرکت">{companyName}</Descriptions.Item>
          ) : null}
          <Descriptions.Item label="وضعیت">
            <Tag color={st.color}>{st.text}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="موبایل">{client?.mobile || '—'}</Descriptions.Item>
          <Descriptions.Item label="شیوه آشنایی">{resolveClientSourceLabel(client) || '—'}</Descriptions.Item>
          <Descriptions.Item label="ایمیل">{client?.email || '—'}</Descriptions.Item>
          <Descriptions.Item label="جنسیت">
            {client?.gender === 'female' ? 'زن' : client?.gender === 'male' ? 'مرد' : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="زبان">{client?.locale || '—'}</Descriptions.Item>
          <Descriptions.Item label="ورود به پنل">
            {client?.login === 'enable' ? 'فعال' : 'غیرفعال'}
          </Descriptions.Item>
          <Descriptions.Item label="تاریخ ایجاد">
            {client?.created_at ? new Date(client.created_at).toLocaleDateString('fa-IR') : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="آخرین ورود">
            {client?.last_login ? new Date(client.last_login).toLocaleString('fa-IR') : '—'}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <MiniBarChart
            title="وضعیت پروژه‌ها"
            labels={projectChart.labels || []}
            values={projectChart.values || []}
            colors={projectChart.colors || []}
          />
        </Col>
        <Col xs={24} lg={12}>
          <MiniBarChart
            title="وضعیت فاکتورها"
            labels={invoiceChart.labels || []}
            values={invoiceChart.values || []}
            colors={invoiceChart.colors || []}
          />
        </Col>
      </Row>
    </div>
  );
};

const formatClientDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString('fa-IR');
};

const getDealStageType = (deal) =>
  getDealStageVisualType(deal?.lead_stage?.name || deal?.lead_stage?.slug);

const getDealCardAccentClass = (deal) => {
  const type = getDealStageType(deal);
  if (type === 'won') return 'border-green-200 bg-green-50/40 dark:border-green-900/50 dark:bg-green-900/10';
  if (type === 'lost') return 'border-red-200 bg-red-50/40 dark:border-red-900/50 dark:bg-red-900/10';
  return 'border-blue-200 bg-blue-50/30 dark:border-blue-900/40 dark:bg-blue-900/10';
};

const resolveDealFollowUpDate = (deal) =>
  deal?.followup?.next_follow_up_date
  || deal?.follow_up?.next_follow_up_date
  || deal?.next_follow_up_date
  || null;

const dealHasFollowUp = (deal) => {
  if (resolveDealFollowUpDate(deal)) return true;
  const flag = deal?.next_follow_up;
  return flag === 'yes' || flag === 1 || flag === true;
};

const formatDealDisplayDate = (value) => {
  if (!value) return '—';
  return formatDescriptiveDate(value) || formatClientDate(value);
};

const getDealDateMeta = (deal) => {
  const type = getDealStageType(deal);
  const followUpDate = resolveDealFollowUpDate(deal);

  if (type === 'won' || type === 'lost') {
    return {
      label: 'تاریخ بستن',
      value: deal.close_date
        ? (formatCloseDateLabel(deal.close_date) || formatClientDate(deal.close_date))
        : '—',
      subtitle: null,
    };
  }

  if (dealHasFollowUp(deal)) {
    return {
      label: 'تاریخ پیگیری بعدی',
      value: followUpDate ? formatDealDisplayDate(followUpDate) : '—',
      subtitle: null,
    };
  }

  return {
    label: 'تاریخ ایجاد',
    value: deal.created_at ? formatDealDisplayDate(deal.created_at) : '—',
    subtitle: 'پیگیری ندارد',
  };
};

const ClientDealsTab = ({ data, navigate, navigateWithTrail, clientId, clientName }) => {
  const deals = Array.isArray(data?.deals) ? data.deals : [];
  const stats = data?.stats || {};
  const [downloading, setDownloading] = useState({});
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [copyingPrompt, setCopyingPrompt] = useState(false);
  const dealsWithNotes = deals.filter((d) => d.note && String(d.note).trim() !== '');

  const handleCopyPrompt = async () => {
    setCopyingPrompt(true);
    try {
      const prompt = buildClientConsultantPrompt({ clientName, deals, stats });
      await navigator.clipboard.writeText(prompt);
      message.success('پرامپت سناریوی تماس کپی شد');
    } catch (e) {
      console.error('copy prompt failed', e);
      message.error('خطا در کپی؛ لطفاً دوباره تلاش کنید');
    } finally {
      setCopyingPrompt(false);
    }
  };

  const handleDownload = async (type, docId) => {
    const key = `${type}-${docId}`;
    setDownloading((prev) => ({ ...prev, [key]: true }));
    try {
      if (type === 'invoice') {
        await financeSettingsApi.downloadInvoice(docId);
      } else {
        await financeSettingsApi.downloadEstimate(docId);
      }
    } catch (e) {
      console.error(e);
      message.error('خطا در دانلود فایل');
    } finally {
      setDownloading((prev) => ({ ...prev, [key]: false }));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-end gap-2">
        {deals.length > 0 && (
          <Button
            icon={<FileTextOutlined />}
            className="border-violet-300 text-violet-600 hover:!text-violet-700 hover:!border-violet-400 dark:border-violet-700 dark:text-violet-300"
            onClick={() => setNotesModalOpen(true)}
          >
            توضیحات همه معاملات
            {dealsWithNotes.length > 0 ? (
              <span className="mr-1.5 inline-flex items-center justify-center rounded-full bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-200 text-[11px] font-bold h-5 min-w-5 px-1.5">
                {dealsWithNotes.length}
              </span>
            ) : null}
          </Button>
        )}
        {clientId && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            className={PANEL_PRIMARY_BTN_CLASS}
            onClick={() =>
              navigate(`/deals/add?client_id=${clientId}`, {
                state: { returnTo: `/clients/${clientId}?tab=deals` },
              })
            }
          >
            ایجاد معامله
          </Button>
        )}
      </div>

      <Modal
        open={notesModalOpen}
        onCancel={() => setNotesModalOpen(false)}
        footer={null}
        width={640}
        title={
          <div className="flex items-center justify-between gap-2 custom-font pl-6">
            <div className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-50 dark:bg-violet-900/30">
                <FileTextOutlined className="text-violet-600 dark:text-violet-300" />
              </span>
              <span className="font-bold">توضیحات معاملات</span>
            </div>
            <Button
              type="primary"
              size="small"
              icon={<CopyOutlined />}
              loading={copyingPrompt}
              onClick={handleCopyPrompt}
              className="bg-violet-600 hover:!bg-violet-700 border-0"
            >
              کپی پرامپت سناریوی تماس
            </Button>
          </div>
        }
        className="custom-font"
      >
        {dealsWithNotes.length ? (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pl-1 -mr-1 pr-1">
            {dealsWithNotes.map((deal) => (
              <div
                key={deal.id}
                className="rounded-xl border border-gray-100 dark:border-gray-700 bg-gray-50/70 dark:bg-gray-800/40 overflow-hidden"
              >
                <div className="flex items-center justify-between gap-2 px-3.5 py-2.5 bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700">
                  <Button
                    type="link"
                    className="p-0 h-auto font-semibold text-sm text-right"
                    onClick={() => {
                      setNotesModalOpen(false);
                      navigateWithTrail(
                        `/deals/${deal.id}`,
                        deal.name || `معامله #${deal.id}`,
                        { returnTo: `/clients/${clientId}?tab=deals` },
                      );
                    }}
                  >
                    {deal.name || `معامله #${deal.id}`}
                  </Button>
                  {deal.lead_stage?.name ? (
                    <Tag
                      className="m-0 text-[11px]"
                      color={getDealStageType(deal) === 'won' ? 'green' : getDealStageType(deal) === 'lost' ? 'red' : 'blue'}
                    >
                      {deal.lead_stage.name}
                    </Tag>
                  ) : null}
                </div>
                <p className="m-0 px-3.5 py-3 text-sm leading-7 text-gray-700 dark:text-gray-200 whitespace-pre-wrap break-words">
                  {deal.note}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <Empty description="هیچ معامله‌ای توضیحات ندارد" />
        )}
      </Modal>
      <Row gutter={[12, 12]}>
        <Col xs={12} sm={8} md={4}>
          <Card size="small" className="border-0 shadow-sm h-full">
            <div className="text-xs text-gray-500 mb-1">کل معاملات</div>
            <div className="text-xl font-bold">{stats.total ?? deals.length}</div>
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card size="small" className="border-0 shadow-sm h-full">
            <div className="flex items-center gap-1 text-xs text-green-600 mb-1">
              <TrophyOutlined />
              <span>موفق</span>
            </div>
            <div className="text-xl font-bold text-green-600">{stats.won_count ?? 0}</div>
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card size="small" className="border-0 shadow-sm h-full">
            <div className="flex items-center gap-1 text-xs text-red-500 mb-1">
              <CloseCircleOutlined />
              <span>ناموفق</span>
            </div>
            <div className="text-xl font-bold text-red-500">{stats.lost_count ?? 0}</div>
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card size="small" className="border-0 shadow-sm h-full">
            <div className="text-xs text-gray-500 mb-1">اولین معامله موفق</div>
            <div className="text-sm font-semibold">
              {stats.first_won_date
                ? formatCloseDateLabel(stats.first_won_date) || formatClientDate(stats.first_won_date)
                : '—'}
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={12} md={6}>
          <Card size="small" className="border-0 shadow-sm h-full">
            <div className="text-xs text-gray-500 mb-1">آخرین معامله موفق</div>
            <div className="text-sm font-semibold">
              {stats.last_won_date
                ? formatCloseDateLabel(stats.last_won_date) || formatClientDate(stats.last_won_date)
                : '—'}
            </div>
          </Card>
        </Col>
      </Row>

      {deals.length ? (
        <div className="space-y-3">
          {deals.map((deal) => {
            const estimates = Array.isArray(deal.estimates) ? deal.estimates : [];
            const invoices = Array.isArray(deal.invoices) ? deal.invoices : [];
            const dateMeta = getDealDateMeta(deal);
            const ownerName = deal.deal_watcher?.name;
            const isWonDeal = getDealStageType(deal) === 'won';
            const products = Array.isArray(deal.products) ? deal.products : [];
            return (
              <div
                key={deal.id}
                className={`rounded-xl border p-3 sm:p-4 shadow-sm transition-shadow hover:shadow-md ${getDealCardAccentClass(deal)}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        type="link"
                        className="p-0 h-auto font-semibold text-sm sm:text-base"
                        onClick={() =>
                          navigateWithTrail(
                            `/deals/${deal.id}`,
                            deal.name || `معامله #${deal.id}`,
                            { returnTo: `/clients/${clientId}?tab=deals` },
                          )
                        }
                      >
                        {deal.name}
                      </Button>
                      {deal.lead_stage?.name ? (
                        <Tag
                          className="m-0 text-xs"
                          color={getDealStageType(deal) === 'won' ? 'green' : getDealStageType(deal) === 'lost' ? 'red' : 'blue'}
                        >
                          {deal.lead_stage.name}
                        </Tag>
                      ) : null}
                      <PriorityStars priority={deal.priority} disabled size={14} />
                    </div>
                    <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-500 dark:text-gray-400">
                      {ownerName && (
                        <span>
                          مالک: <span className="text-gray-700 dark:text-gray-200 font-medium">{ownerName}</span>
                        </span>
                      )}
                      <span>
                        ارزش: <span className="text-gray-800 dark:text-gray-100 font-medium">{formatPriceToman(deal.value)}</span>
                      </span>
                      {deal.success_rate != null && (
                        <span>
                          احتمال موفقیت: <span className="font-medium">{deal.success_rate}%</span>
                        </span>
                      )}
                    </div>
                    {deal.tags?.length > 0 && (
                      <EntityTagsList tags={deal.tags} size="sm" maxVisible={4} className="mt-2" />
                    )}
                    {isWonDeal && products.length > 0 && (
                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        <ShoppingOutlined className="text-green-600 text-xs shrink-0" />
                        <span className="text-xs text-gray-500 dark:text-gray-400">محصولات خریداری‌شده:</span>
                        {products.map((product) => (
                          <Tag key={product.id} color="green" className="m-0 text-xs">
                            {product.name}
                          </Tag>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-500 shrink-0">
                    <div>{dateMeta.label}</div>
                    <div className="font-semibold text-gray-800 dark:text-gray-100">{dateMeta.value}</div>
                    {dateMeta.subtitle ? (
                      <div className="text-[11px] text-amber-600 dark:text-amber-400 mt-0.5">{dateMeta.subtitle}</div>
                    ) : null}
                  </div>
                </div>

                {(estimates.length > 0 || invoices.length > 0) && (
                  <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700/60 flex flex-wrap gap-2">
                    {estimates.map((r) => (
                      <Button
                        key={`est-${r.id}`}
                        size="small"
                        icon={<DownloadOutlined />}
                        loading={downloading[`estimate-${r.id}`]}
                        onClick={() => handleDownload('estimate', r.id)}
                      >
                        {r.estimate_number || `پیش‌فاکتور #${r.id}`}
                      </Button>
                    ))}
                    {invoices.map((r) => (
                      <Button
                        key={`inv-${r.id}`}
                        size="small"
                        icon={<DownloadOutlined />}
                        loading={downloading[`invoice-${r.id}`]}
                        onClick={() => handleDownload('invoice', r.id)}
                      >
                        {r.invoice_number || `فاکتور #${r.id}`}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <Empty description="معامله‌ای ثبت نشده" />
      )}
    </div>
  );
};

const InvoicesTab = ({ data, clientId, onRefresh, navigateWithTrail }) => {
  const invoices = data?.invoices?.invoices || data?.invoices || [];
  const list = Array.isArray(invoices) ? invoices : [];

  return (
    <EmbeddedInvoicesTable
      invoices={list}
      showDealColumn
      showProjectColumn={false}
      clientId={clientId}
      onChanged={onRefresh}
      createEnabled
      navigateWithTrail={navigateWithTrail}
      returnTo={clientId ? `/clients/${clientId}?tab=invoices` : undefined}
      actionsIconOnly
    />
  );
};

const EstimatesTab = ({ data, clientId, onRefresh, navigateWithTrail }) => {
  const estimates = data?.estimates?.estimates || data?.estimates || [];
  const list = Array.isArray(estimates) ? estimates : [];

  return (
    <EmbeddedEstimatesTable
      estimates={list}
      showDealColumn
      showProjectColumn={false}
      clientId={clientId}
      onChanged={onRefresh}
      createEnabled
      navigateWithTrail={navigateWithTrail}
      returnTo={clientId ? `/clients/${clientId}?tab=estimates` : undefined}
      actionsIconOnly
    />
  );
};

const ProjectsTab = ({ data }) => {
  const projects = data?.projects || [];
  const list = Array.isArray(projects) ? projects : [];

  const columns = [
    { title: 'نام پروژه', dataIndex: 'project_name', key: 'project_name' },
    { title: 'کد', dataIndex: 'project_short_code', key: 'project_short_code' },
    { title: 'وضعیت', dataIndex: 'status', key: 'status', render: (s) => <Tag>{s || '—'}</Tag> },
    {
      title: 'پیشرفت',
      dataIndex: 'completion_percent',
      key: 'completion_percent',
      render: (v) => `${v ?? 0}%`,
    },
    {
      title: 'اعضا',
      key: 'members',
      render: (_, r) => (
        <Avatar.Group maxCount={4} size="small">
          {(r.members || []).map((m) => (
            <Avatar key={m.id} src={getImagePath(m.user?.image_url)} title={m.user?.name}>
              {(m.user?.name || '?').charAt(0)}
            </Avatar>
          ))}
        </Avatar.Group>
      ),
    },
  ];

  return list.length ? (
    <Table rowKey="id" columns={columns} dataSource={list} pagination={{ pageSize: 10 }} scroll={{ x: 800 }} />
  ) : (
    <Empty description="پروژه‌ای ثبت نشده" />
  );
};

const OrdersTab = ({ data }) => {
  const orders = data?.orders || [];
  const list = Array.isArray(orders) ? orders : [];

  const columns = [
    { title: 'شماره سفارش', dataIndex: 'custom_order_number', key: 'order_number', render: (v, r) => v || r.order_number },
    {
      title: 'تاریخ',
      dataIndex: 'order_date',
      key: 'order_date',
      render: (v) => (v ? toJalali(v) || '—' : '—'),
    },
    {
      title: 'مبلغ',
      dataIndex: 'total',
      key: 'total',
      render: (v) => formatPriceToman(v),
    },
    {
      title: 'وضعیت',
      dataIndex: 'status',
      key: 'status',
      render: (s) => <Tag>{s || '—'}</Tag>,
    },
  ];

  return list.length ? (
    <Table rowKey="id" columns={columns} dataSource={list} pagination={{ pageSize: 10 }} scroll={{ x: 700 }} />
  ) : (
    <Empty description="سفارشی ثبت نشده" />
  );
};

const TicketsTab = ({ data }) => {
  const tickets = data?.tickets || [];
  const list = Array.isArray(tickets) ? tickets : [];

  const columns = [
    { title: 'شماره', dataIndex: 'ticket_number', key: 'ticket_number' },
    { title: 'موضوع', dataIndex: 'subject', key: 'subject', ellipsis: true },
    { title: 'تاریخ', dataIndex: 'created_on', key: 'created_on' },
    {
      title: 'اولویت',
      dataIndex: 'priority',
      key: 'priority',
      render: (p) => <Tag color={p === 'urgent' ? 'red' : 'default'}>{p || '—'}</Tag>,
    },
    {
      title: 'وضعیت',
      dataIndex: 'status',
      key: 'status',
      render: (s) => <Tag color={s === 'open' ? 'blue' : 'default'}>{s || '—'}</Tag>,
    },
  ];

  return list.length ? (
    <Table rowKey="id" columns={columns} dataSource={list} pagination={{ pageSize: 10 }} scroll={{ x: 800 }} />
  ) : (
    <Empty description="تیکتی ثبت نشده" />
  );
};

const DocumentsTab = ({ data }) => {
  const documents = data?.documents || [];
  const list = Array.isArray(documents) ? documents : [];

  const columns = [
    { title: 'نام', dataIndex: 'name', key: 'name' },
    { title: 'فایل', dataIndex: 'filename', key: 'filename', ellipsis: true },
    {
      title: 'حجم',
      dataIndex: 'size',
      key: 'size',
      render: (s) => (s ? `${(Number(s) / 1024).toFixed(1)} KB` : '—'),
    },
    {
      title: 'عملیات',
      key: 'actions',
      width: 120,
      render: (_, r) =>
        r.doc_url ? (
          <Button type="link" icon={<DownloadOutlined />} href={r.doc_url} target="_blank" rel="noopener noreferrer">
            دانلود
          </Button>
        ) : (
          '—'
        ),
    },
  ];

  return list.length ? (
    <Table rowKey="id" columns={columns} dataSource={list} pagination={{ pageSize: 10 }} scroll={{ x: 700 }} />
  ) : (
    <Empty description="سندی ثبت نشده" />
  );
};

const ViewClient = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { returnToRef, buildGoBackState } = useDetailReturnRefs();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'deals');
  const [clientHeader, setClientHeader] = useState(null);
  const [clientDetails, setClientDetails] = useState(null);
  const [tabData, setTabData] = useState(null);
  const [tabCache, setTabCache] = useState({});
  const [smsModalOpen, setSmsModalOpen] = useState(false);
  const [smsMessage, setSmsMessage] = useState('');
  const [smsLoading, setSmsLoading] = useState(false);

  const { trail, navigateWithTrail, goToCrumb } = useEntityBreadcrumb({
    listPath: '/clients',
    listLabel: 'مشتریان',
    entityLabel: clientHeader?.name || tabData?.client?.name || (id ? `مشتری #${id}` : 'مشتری'),
  });

  const loadTab = useCallback(async (tab, { silent = false, force = false } = {}) => {
    if (tab === 'sms-history') {
      setLoading(false);
      return;
    }
    if (!force && tabCache[tab]) {
      const cached = tabCache[tab];
      setTabData(cached);
      if (cached?.client) setClientHeader(cached.client);
      const cachedDetails = extractClientDetails(cached);
      if (cachedDetails) setClientDetails(cachedDetails);
      return;
    }
    if (!silent) setTabLoading(true);
    try {
      const res = await clientsApi.getById(id, tab);
      const td = res?.tab_data ?? res?.data?.tab_data ?? res?.data;
      setTabData(td);
      setTabCache((prev) => ({ ...prev, [tab]: td }));

      if (td?.client) {
        setClientHeader(td.client);
      }
      const details = extractClientDetails(td);
      if (details) {
        setClientDetails(details);
      }
    } catch (e) {
      console.error(e);
      message.error('خطا در دریافت اطلاعات');
    } finally {
      if (!silent) setTabLoading(false);
      setLoading(false);
    }
  }, [id, tabCache]);

  const loadProfileForHeader = useCallback(async () => {
    try {
      const res = await clientsApi.getById(id, 'profile');
      const td = res?.tab_data ?? res?.data?.tab_data ?? res?.data;
      if (td?.client) {
        setClientHeader(td.client);
      }
      const details = extractClientDetails(td);
      if (details) {
        setClientDetails(details);
      }
      setTabCache((prev) => ({ ...prev, profile: td }));
      return td;
    } catch (e) {
      console.error(e);
      return null;
    }
  }, [id]);

  useEffect(() => {
    setLoading(true);
    setTabCache({});
    setClientDetails(null);
    const initialTab = searchParams.get('tab') || 'deals';
    setActiveTab(initialTab);

    const init = async () => {
      if (initialTab !== 'profile') {
        await loadProfileForHeader();
      }
      if (initialTab === 'sms-history') {
        setLoading(false);
        return;
      }
      await loadTab(initialTab, { force: true });
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
      if (tabFromUrl !== 'sms-history') {
        loadTab(tabFromUrl);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', tab);
        return next;
      },
      { replace: true },
    );
    if (tab !== 'sms-history') {
      loadTab(tab);
    }
  };

  const client = clientHeader || tabData?.client;
  const companyName = resolveClientCompanyName(
    { client_details: clientDetails },
    client,
    tabData,
    clientHeader,
  );
  const st = statusMeta[client?.status] || { text: client?.status, color: 'default' };
  const sourceLabel = resolveClientSourceLabel(
    clientDetails
      ? { ...(client || {}), client_details: client?.client_details || clientDetails }
      : client,
  );

  if (loading && !client) {
    return (
      <MainLayout>
        <div className="flex justify-center py-24">
          <Spin size="large" />
        </div>
      </MainLayout>
    );
  }

  const tabItems = [
    {
      key: 'profile',
      label: (
        <span className="flex items-center gap-1">
          <TeamOutlined />
          پروفایل
        </span>
      ),
      children: <ProfileTab tabData={tabData} companyName={companyName} />,
    },
    {
      key: 'deals',
      label: (
        <span className="flex items-center gap-1">
          <FundOutlined />
          معاملات
        </span>
      ),
      children: (
        <ClientDealsTab
          data={tabData}
          navigate={navigate}
          navigateWithTrail={navigateWithTrail}
          clientId={id}
          clientName={client?.name}
        />
      ),
    },
    {
      key: 'invoices',
      label: (
        <span className="flex items-center gap-1">
          <DollarOutlined />
          فاکتورها
        </span>
      ),
      children: (
        <InvoicesTab
          data={tabData}
          clientId={id}
          onRefresh={() => loadTab('invoices', { force: true })}
          navigateWithTrail={navigateWithTrail}
        />
      ),
    },
    {
      key: 'estimates',
      label: (
        <span className="flex items-center gap-1">
          <FileTextOutlined />
          پیش‌فاکتورها
        </span>
      ),
      children: (
        <EstimatesTab
          data={tabData}
          clientId={id}
          onRefresh={() => loadTab('estimates', { force: true })}
          navigateWithTrail={navigateWithTrail}
        />
      ),
    },
    {
      key: 'projects',
      label: (
        <span className="flex items-center gap-1">
          <ProjectOutlined />
          پروژه‌ها
        </span>
      ),
      children: <ProjectsTab data={tabData} />,
    },
    {
      key: 'orders',
      label: (
        <span className="flex items-center gap-1">
          <ShoppingCartOutlined />
          سفارشات
        </span>
      ),
      children: <OrdersTab data={tabData} />,
    },
    {
      key: 'tickets',
      label: (
        <span className="flex items-center gap-1">
          <CustomerServiceOutlined />
          تیکت‌ها
        </span>
      ),
      children: <TicketsTab data={tabData} />,
    },
    {
      key: 'documents',
      label: (
        <span className="flex items-center gap-1">
          <FolderOutlined />
          اسناد
        </span>
      ),
      children: <DocumentsTab data={tabData} />,
    },
    {
      key: 'sms-history',
      label: (
        <span className="flex items-center gap-1">
          <MessageOutlined />
          پیامک‌ها
        </span>
      ),
      children: (
        <SmsHistoryPanel
          entityType="client"
          entityId={id}
          recipientName={client?.name}
        />
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="custom-font" style={{ fontFamily: 'CustomFont' }}>
        <Card className="shadow-xl border-0" bodyStyle={{ padding: 0 }}>
          <EntityBreadcrumb trail={trail} onNavigate={goToCrumb} />
          <div className="flex items-center justify-between flex-wrap gap-4 p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 rounded-t-xl">
            <div className="flex items-center gap-3 min-w-0">
              <Avatar size={56} src={getImagePath(client?.image_url || client?.image)} className="flex-shrink-0">
                {(client?.name || '?').charAt(0)}
              </Avatar>
              <div className="min-w-0">
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100 m-0 truncate">
                  {client?.name || '—'}
                  {companyName ? (
                    <span className="text-base font-medium text-gray-500 dark:text-gray-400 mr-2">
                      · {companyName}
                    </span>
                  ) : null}
                </h1>
                <Space className="mt-1 flex-wrap" size="middle">
                  {client?.status ? <Tag color={st.color}>{st.text}</Tag> : null}
                  {client?.mobile ? (
                    <span className="flex items-center gap-1 text-gray-600 dark:text-gray-300 text-sm">
                      <PhoneOutlined className="text-gray-400" />
                      <span dir="ltr">{client.mobile}</span>
                      <CopyIconButton text={client.mobile} />
                      <ClickToCallButton phone={client.mobile} />
                    </span>
                  ) : null}
                  {sourceLabel ? (
                    <span className="flex items-center gap-1 text-gray-600 dark:text-gray-300 text-sm">
                      <LinkOutlined className="text-gray-400" />
                      <span>شیوه آشنایی: {sourceLabel}</span>
                    </span>
                  ) : null}
                  {client?.email ? <span className="text-gray-500 text-sm">{client.email}</span> : null}
                </Space>
              </div>
            </div>
            <Space wrap className="flex-shrink-0">
              <Button
                icon={<ArrowRightOutlined />}
                onClick={() => navigate(returnToRef.current || '/clients', { state: buildGoBackState() })}
              >
                بازگشت
              </Button>
              <Button
                icon={<MessageOutlined />}
                onClick={() => {
                  setSmsMessage('');
                  setSmsModalOpen(true);
                }}
              >
                ارسال پیامک
              </Button>
              <PermissionGate permission="edit_clients">
                <Button
                  type="primary"
                  icon={<EditOutlined />}
                  className={PANEL_PRIMARY_BTN_CLASS}
                  onClick={() => navigate(`/clients/${id}/edit`)}
                >
                  ویرایش
                </Button>
              </PermissionGate>
            </Space>
          </div>

          <div className="p-4 sm:p-6 bg-gray-50 dark:bg-gray-900/50 rounded-b-xl">
            <Spin spinning={tabLoading}>
              <Tabs
                activeKey={activeTab}
                onChange={handleTabChange}
                items={tabItems}
                className="client-view-tabs"
                tabBarStyle={{ marginBottom: 16 }}
              />
            </Spin>
          </div>
        </Card>

        <Modal
          title={<span style={{ fontFamily: 'CustomFont' }}>ارسال پیامک</span>}
          open={smsModalOpen}
          onCancel={() => {
            setSmsModalOpen(false);
            setSmsMessage('');
          }}
          onOk={async () => {
            const text = String(smsMessage || '').trim();
            if (!text) {
              message.warning('لطفاً متن پیامک را وارد کنید');
              return;
            }
            setSmsLoading(true);
            try {
              await clientsApi.applyQuickAction('send-sms', [id], { message: text });
              message.success('پیامک ارسال شد');
              setSmsModalOpen(false);
              setSmsMessage('');
            } catch (e) {
              message.error(e?.message || 'خطا در ارسال پیامک');
            } finally {
              setSmsLoading(false);
            }
          }}
          okText="ارسال"
          cancelText="لغو"
          confirmLoading={smsLoading}
          destroyOnClose
        >
          <div className="py-2">
            <label
              className="block mb-2 text-sm text-gray-700 dark:text-gray-300"
              style={{ fontFamily: 'CustomFont' }}
            >
              متن پیامک{client?.name ? ` برای ${client.name}` : ''}
              {client?.mobile ? ` (${client.mobile})` : ''}
            </label>
            <Input.TextArea
              value={smsMessage}
              onChange={(e) => setSmsMessage(e.target.value)}
              rows={4}
              placeholder="متن پیامک را وارد کنید"
              style={{ fontFamily: 'CustomFont' }}
            />
          </div>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default ViewClient;
